import { useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogTrigger, Popover } from 'react-aria-components';
import { UseFormRegisterReturn } from 'react-hook-form';
import { Icon } from '../atoms';
import { Tag } from './Tag';

export type MultiSelectDropdownOption = {
  label: string;
  description?: string;
};

type MultiSelectDropdownProps = {
  options: MultiSelectDropdownOption[];
  selectedItems: string[];
  onSelectionChange: (selectedItems: string[]) => void;
  selectionLimit?: number;
  label?: string;
  error?: string;
  placeholder?: string;
  register?: () => UseFormRegisterReturn<string>;
  filterable?: boolean;
};

export const MultiSelectDropdown = (props: MultiSelectDropdownProps) => {
  const {
    options,
    onSelectionChange,
    placeholder,
    label,
    selectionLimit,
    selectedItems,
    error,
    register,
    filterable = false,
  } = props;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const divRef = useRef<HTMLDivElement>(null);

  const handleCheckboxChange = (option: MultiSelectDropdownOption) => {
    const label = option.label;

    if (selectedItems.includes(label)) {
      onSelectionChange(selectedItems.filter((i) => i !== label).sort());
    } else if (!selectionLimit || selectedItems.length < selectionLimit) {
      onSelectionChange([...selectedItems, label].sort());
    }
  };

  const handleItemRemove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, label: string) => {
    event.stopPropagation();
    event.preventDefault();
    onSelectionChange(selectedItems.filter((i) => i !== label));
  };

  const filteredOptions = useMemo(() => {
    if (!filterable || !filter.trim()) return options;
    const lower = filter.trim().toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(lower) ||
        (opt.description && opt.description.toLowerCase().includes(lower)),
    );
  }, [options, filter, filterable]);

  return (
    <DialogTrigger
      onOpenChange={(isOpen) => {
        setDropdownOpen(isOpen);
      }}
    >
      <div
        className={`w-full mx-auto flex flex-col justify-center gap-1 max-h-[596px] overflow-auto`}
        data-testid='multi-select-dropdown'
        {...(register ? register() : {})}
      >
        <div className='space-y-1 w-full flex flex-col justify-center items-start text-left'>
          <div className='text-sm font-medium text-gray-700'>{label}</div>
          <Button name={label} className={'w-full'}>
            <div
              className={`mt-1 px-3 py-2 border border-gray-200
          rounded-md bg-white text-sm text-gray-700 min-h-[42px]
          cursor-pointer flex justify-between items-center
          text-ellipsis  ${dropdownOpen ? 'ring-brand-shadow-xs border-secondary-brand' : 'p-2'}`}
              ref={divRef}
            >
              <span className='overflow-hidden text-ellipsis whitespace-nowrap flex flex-wrap gap-1 rounded-md'>
                {selectedItems.length > 0 ?
                  selectedItems.map((item) => {
                    return (
                      <Tag
                        key={item}
                        size='sm'
                        tailIcon='x-close'
                        onTailIconClick={(event) => handleItemRemove(event, item)}
                      >
                        <div>{item}</div>
                      </Tag>
                    );
                  })
                : <div className='text-gray-400'>{placeholder}</div>}
              </span>
              {dropdownOpen ?
                <Icon type='chevron-up' size={'18'} />
              : <Icon type='chevron-down' size={'18'} />}
            </div>
          </Button>
        </div>
        <Popover
          placement='bottom'
          className='-mt-2 w-full rounded-md border border-gray-300 bg-white shadow-sm max-h-64 overflow-y-auto'
          style={{ width: divRef.current?.getBoundingClientRect().width }}
        >
          <Dialog className='p-1 w-full max-h-64 overflow-y-auto'>
            {filterable && (
              <div className='my-2 px-2'>
                <input
                  type='text'
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder='Type to filter...'
                  className='w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm'
                  autoFocus
                />
              </div>
            )}
            {filteredOptions.length === 0 && (
              <div className='text-gray-400 text-sm px-2 py-4 text-center'>No options found</div>
            )}
            {filteredOptions.map((option) => {
              const optionLabel = option.label;
              const disabled =
                !!selectionLimit &&
                !selectedItems.includes(optionLabel) &&
                selectedItems.length >= selectionLimit;
              return (
                <div
                  key={optionLabel}
                  role='listitem'
                  className={`flex items-center gap-1 ${option.description ? 'h-[62px]' : 'h-10'} py-1 px-2 select-none hover:cursor-pointer hover:bg-gray-50`}
                  onClick={(_e) => {
                    if (disabled) return;
                    handleCheckboxChange(option);
                  }}
                >
                  <input
                    type='checkbox'
                    id={optionLabel}
                    checked={selectedItems.includes(optionLabel)}
                    disabled={disabled}
                    onChange={() => handleCheckboxChange(option)}
                    className='mr-2 hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 h-5 w-5 rounded border-gray-300 focus:ring-brand-500 focus:ring-2'
                  />
                  <div className='flex flex-col gap-1'>
                    <label
                      htmlFor={optionLabel}
                      className='text-sm text-gray-500 font-medium hover:cursor-pointer pointer-events-none'
                    >
                      {optionLabel}
                      {disabled && (
                        <span className='text-xs text-red-500 ml-2'>(Limit reached)</span>
                      )}
                    </label>
                    {option.description && (
                      <div className=' text-sm text-gray-400'>{option.description}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </Dialog>
        </Popover>
        {error && <div className='text-sm text-error-500 p-1'>{error}</div>}
      </div>
    </DialogTrigger>
  );
};
